import type { GradingModel, GradingScale, HonoursRule } from '@/types/config-governance';

/**
 * The client-side mirror of GradingStrategyService.validate on core.
 *
 * Mirrored so the band editor can show every problem AS THE ADMIN TYPES —
 * a gap appears hatched in the scale drawing and named under it in the same
 * keystroke — instead of on the round trip to POST :id/validate. The server
 * remains the authority: the same checks gate activation there, and the report
 * endpoint's sentences are shown verbatim on save. What this mirror must
 * therefore guarantee is AGREEMENT, which is why every numeric comparison
 * below copies the server's arithmetic exactly:
 *
 * BAND ADJACENCY. Both band ends are inclusive, so two conventions are legal
 * and both appear in real ministerial tables — shared boundaries (0–10, 10–20)
 * and stepped ones (0–9.99, 10–20). A gap is "more than one smallest
 * representable step between bands", where the step follows decimalPlaces; an
 * overlap is any interior intersection. Comparisons are done in integer step
 * units so 0.1 + 0.2 floating-point noise cannot manufacture either error on
 * one side of the wire and not the other.
 *
 * Issues are returned as {code, params} rather than sentences, because the
 * live report renders in the operator's language (EN/FR) while the server's
 * sentences arrive only in English. The union of codes is closed; the page
 * owns the wording under grading.validation.*.
 */

export interface StrategyShape {
  gradingModel: GradingModel | string;
  gradingScale: GradingScale | string;
  minScore: number;
  maxScore: number;
  passThreshold: number;
  decimalPlaces: number;
  bands: {
    code: string;
    label: string;
    minInclusive: number;
    maxInclusive: number;
    gradePoints?: number | null;
  }[];
  honours?: HonoursRule[] | null;
}

export type StrategyIssueCode =
  | 'minNotBelowMax'
  | 'noBands'
  | 'bandInverted'
  | 'bandOutsideRange'
  | 'duplicateBandCode'
  | 'lowestBandStartsLate'
  | 'highestBandEndsEarly'
  | 'bandsOverlap'
  | 'bandsGap'
  | 'thresholdOutsideRange'
  | 'thresholdBetweenBands'
  | 'missingGradePoints'
  | 'notTwoBands'
  | 'honoursOutsideRange';

export interface StrategyIssue {
  code: StrategyIssueCode;
  /** Interpolation values for the translated sentence — band codes, numbers. */
  params: Record<string, string | number>;
}

/** Scales whose bands MUST carry grade points — a GPA without points computes nothing. */
const POINT_BEARING_SCALES: readonly string[] = ['GPA', 'CGPA'];

/** Verdict scales: exactly two outcomes, by definition. */
const TWO_BAND_SCALES: readonly string[] = ['PASS_FAIL', 'COMPETENT_NOT_YET_COMPETENT'];

export function validateStrategyDraft(strategy: StrategyShape): StrategyIssue[] {
  const issues: StrategyIssue[] = [];
  const decimalPlaces = Number.isInteger(strategy.decimalPlaces) ? strategy.decimalPlaces : 0;
  const step = Math.pow(10, -decimalPlaces);
  const inSteps = (value: number): number => Math.round(value / step);

  if (!(strategy.minScore < strategy.maxScore)) {
    issues.push({
      code: 'minNotBelowMax',
      params: { min: strategy.minScore, max: strategy.maxScore },
    });
  }

  const bands = [...strategy.bands];
  if (bands.length === 0) {
    issues.push({ code: 'noBands', params: {} });
    // The server returns here too: every later check dereferences a band.
    return issues;
  }

  for (const band of bands) {
    if (band.minInclusive > band.maxInclusive) {
      issues.push({
        code: 'bandInverted',
        params: { band: band.code, min: band.minInclusive, max: band.maxInclusive },
      });
    }
    if (band.minInclusive < strategy.minScore || band.maxInclusive > strategy.maxScore) {
      issues.push({
        code: 'bandOutsideRange',
        params: {
          band: band.code,
          bandMin: band.minInclusive,
          bandMax: band.maxInclusive,
          min: strategy.minScore,
          max: strategy.maxScore,
        },
      });
    }
  }

  const codes = new Set<string>();
  for (const band of bands) {
    if (codes.has(band.code)) {
      issues.push({ code: 'duplicateBandCode', params: { band: band.code } });
    }
    codes.add(band.code);
  }

  bands.sort((a, b) => a.minInclusive - b.minInclusive || a.maxInclusive - b.maxInclusive);

  if (inSteps(bands[0].minInclusive) !== inSteps(strategy.minScore)) {
    issues.push({
      code: 'lowestBandStartsLate',
      params: { start: bands[0].minInclusive, min: strategy.minScore },
    });
  }
  const last = bands[bands.length - 1];
  if (inSteps(last.maxInclusive) !== inSteps(strategy.maxScore)) {
    issues.push({
      code: 'highestBandEndsEarly',
      params: { end: last.maxInclusive, max: strategy.maxScore },
    });
  }
  for (let i = 1; i < bands.length; i += 1) {
    const prev = bands[i - 1];
    const next = bands[i];
    const separation = inSteps(next.minInclusive) - inSteps(prev.maxInclusive);
    if (separation < 0) {
      issues.push({ code: 'bandsOverlap', params: { first: prev.code, second: next.code } });
    } else if (separation > 1) {
      issues.push({
        code: 'bandsGap',
        params: {
          first: prev.code,
          firstEnd: prev.maxInclusive,
          second: next.code,
          secondStart: next.minInclusive,
        },
      });
    }
  }

  if (strategy.passThreshold < strategy.minScore || strategy.passThreshold > strategy.maxScore) {
    issues.push({
      code: 'thresholdOutsideRange',
      params: { threshold: strategy.passThreshold, min: strategy.minScore, max: strategy.maxScore },
    });
  } else {
    const t = inSteps(strategy.passThreshold);
    const onABand = bands.some(
      (band) => t >= inSteps(band.minInclusive) && t <= inSteps(band.maxInclusive),
    );
    const onABoundary = bands.some(
      (band) => t === inSteps(band.minInclusive) || t === inSteps(band.maxInclusive),
    );
    if (!onABand && !onABoundary) {
      issues.push({
        code: 'thresholdBetweenBands',
        params: { threshold: strategy.passThreshold },
      });
    }
  }

  const needsPoints =
    POINT_BEARING_SCALES.includes(strategy.gradingScale) || strategy.gradingModel === 'GPA_CGPA';
  if (needsPoints) {
    for (const band of bands) {
      if (band.gradePoints === null || band.gradePoints === undefined) {
        issues.push({
          code: 'missingGradePoints',
          params: { band: band.code, scale: strategy.gradingScale, model: strategy.gradingModel },
        });
      }
    }
  }

  if (TWO_BAND_SCALES.includes(strategy.gradingScale) && bands.length !== 2) {
    issues.push({
      code: 'notTwoBands',
      params: { scale: strategy.gradingScale, total: bands.length },
    });
  }

  for (const rule of strategy.honours ?? []) {
    if (rule.minAverage < strategy.minScore || rule.minAverage > strategy.maxScore) {
      issues.push({
        code: 'honoursOutsideRange',
        params: {
          classification: rule.classification,
          minAverage: rule.minAverage,
          min: strategy.minScore,
          max: strategy.maxScore,
        },
      });
    }
  }

  return issues;
}
